import type { Game, Opponent, Player, Season, Snapshot, StatEvent, ID } from '../domain/types'
import type { Repo } from './repo'

const DB_NAME = 'hustle-stats'
const DB_VERSION = 1

type StoreName = 'players' | 'opponents' | 'seasons' | 'games' | 'events' | 'meta'

const STORES: StoreName[] = ['players', 'opponents', 'seasons', 'games', 'events', 'meta']

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'))
  })
}

/**
 * WebKit has a long history of indexedDB.open never firing any event on a cold
 * start or a bfcache restore. A timeout turns that hang into a rejection so the
 * caller can fall back to localStorage instead of showing a permanently blank
 * screen mid-game.
 */
function openWithTimeout(ms: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false
    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      fn()
    }
    const timer = setTimeout(() => {
      finish(() => reject(new Error('indexedDB.open timed out')))
    }, ms)

    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch (err) {
      finish(() => reject(err))
      return
    }

    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('players')) db.createObjectStore('players', { keyPath: 'id' })
      if (!db.objectStoreNames.contains('opponents')) db.createObjectStore('opponents', { keyPath: 'id' })
      if (!db.objectStoreNames.contains('seasons')) db.createObjectStore('seasons', { keyPath: 'id' })
      if (!db.objectStoreNames.contains('games')) {
        const games = db.createObjectStore('games', { keyPath: 'id' })
        games.createIndex('bySeason', 'seasonId')
        games.createIndex('byStatus', 'status')
      }
      if (!db.objectStoreNames.contains('events')) {
        const events = db.createObjectStore('events', { keyPath: 'id' })
        events.createIndex('byGame', 'gameId')
      }
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta')
    }
    req.onsuccess = () => finish(() => resolve(req.result))
    req.onerror = () => finish(() => reject(req.error ?? new Error('indexedDB.open failed')))
    req.onblocked = () => finish(() => reject(new Error('indexedDB.open blocked')))
  })
}

export class IdbRepo implements Repo {
  readonly kind = 'indexeddb' as const
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    const db = await openWithTimeout(4000)
    // A version change from another tab would make every later transaction throw.
    db.onversionchange = () => db.close()
    this.db = db
    // Probe a real transaction: open() can succeed on Safari while reads still fail.
    await this.all<Player>('players')
  }

  private tx(stores: StoreName | StoreName[], mode: IDBTransactionMode): IDBTransaction {
    if (!this.db) throw new Error('repo not initialised')
    return this.db.transaction(stores, mode)
  }

  private async all<T>(store: StoreName): Promise<T[]> {
    const tx = this.tx(store, 'readonly')
    return promisify(tx.objectStore(store).getAll() as IDBRequest<T[]>)
  }

  private async put(store: StoreName, value: unknown, key?: IDBValidKey): Promise<void> {
    const tx = this.tx(store, 'readwrite')
    tx.objectStore(store).put(value, key)
    await txDone(tx)
  }

  listPlayers() {
    return this.all<Player>('players')
  }
  savePlayer(p: Player) {
    return this.put('players', p)
  }

  async deletePlayer(id: ID): Promise<void> {
    const tx = this.tx('players', 'readwrite')
    tx.objectStore('players').delete(id)
    await txDone(tx)
  }

  listOpponents() {
    return this.all<Opponent>('opponents')
  }
  saveOpponent(o: Opponent) {
    return this.put('opponents', o)
  }

  listSeasons() {
    return this.all<Season>('seasons')
  }
  saveSeason(s: Season) {
    return this.put('seasons', s)
  }

  listGames() {
    return this.all<Game>('games')
  }
  saveGame(g: Game) {
    return this.put('games', g)
  }

  async getGame(id: ID): Promise<Game | undefined> {
    const tx = this.tx('games', 'readonly')
    return promisify(tx.objectStore('games').get(id) as IDBRequest<Game | undefined>)
  }

  async deleteGame(id: ID): Promise<void> {
    const tx = this.tx(['games', 'events'], 'readwrite')
    tx.objectStore('games').delete(id)
    const cursorReq = tx.objectStore('events').index('byGame').openCursor(IDBKeyRange.only(id))
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result
      if (cursor) {
        cursor.delete()
        cursor.continue()
      }
    }
    await txDone(tx)
  }

  async listEventsByGame(gameId: ID): Promise<StatEvent[]> {
    const tx = this.tx('events', 'readonly')
    const index = tx.objectStore('events').index('byGame')
    return promisify(index.getAll(IDBKeyRange.only(gameId)) as IDBRequest<StatEvent[]>)
  }

  listAllEvents() {
    return this.all<StatEvent>('events')
  }
  saveEvent(e: StatEvent) {
    return this.put('events', e)
  }

  async saveEvents(events: StatEvent[]): Promise<void> {
    if (events.length === 0) return
    const tx = this.tx('events', 'readwrite')
    const store = tx.objectStore('events')
    for (const e of events) store.put(e)
    await txDone(tx)
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    const tx = this.tx('meta', 'readonly')
    return promisify(tx.objectStore('meta').get(key) as IDBRequest<T | undefined>)
  }

  setMeta<T>(key: string, value: T) {
    return this.put('meta', value, key)
  }

  async exportAll(): Promise<Snapshot> {
    const [players, opponents, seasons, games, events] = await Promise.all([
      this.listPlayers(),
      this.listOpponents(),
      this.listSeasons(),
      this.listGames(),
      this.listAllEvents(),
    ])
    return { version: 1, exportedAt: Date.now(), players, opponents, seasons, games, events }
  }

  async importAll(snapshot: Snapshot): Promise<void> {
    const tx = this.tx(['players', 'opponents', 'seasons', 'games', 'events'], 'readwrite')
    for (const p of snapshot.players) tx.objectStore('players').put(p)
    for (const o of snapshot.opponents) tx.objectStore('opponents').put(o)
    for (const s of snapshot.seasons) tx.objectStore('seasons').put(s)
    for (const g of snapshot.games) tx.objectStore('games').put(g)
    for (const e of snapshot.events) tx.objectStore('events').put(e)
    await txDone(tx)
  }

  async clearAll(): Promise<void> {
    const tx = this.tx(STORES, 'readwrite')
    for (const s of STORES) tx.objectStore(s).clear()
    await txDone(tx)
  }
}
